import { Popper } from '@material-ui/core';
import clsx from 'clsx';
import React, { useRef } from 'react';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import { CheckboxFilterBox } from '@/src/components/common/FilterBox/V2/CheckboxFilterBox';
import { CheckboxFilterOption } from '@/src/components/common/FilterBox/V2/FilterBox';
import { useMyPortfolio } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/store/myPortfolio.store';
import { useToggle } from '@/src/hooks/useToggle';
import { SortOrder } from '@/src/util/filters';

import { getSortIndicator } from './HeaderItem';

import FilterCount from '../../../../../Elements/FilterCount/FilterCount';
import { ColumnOption } from '../../Table/Table';

type CheckboxHeaderItemProps = {
  column: ColumnOption;
  filterOption: CheckboxFilterOption;
  filterCountSlot?: React.ReactNode;
  bottomContent?: React.ReactNode;
  disableFilters?: boolean;
  onReset?: () => void;
  onUpdateFilters?: (value: CheckboxFilterOption['value']) => void;
  isForceFilterActive?: boolean;
  isForcedChecked?: boolean;
};

const CheckboxHeaderItem = ({
  column,
  filterOption,
  filterCountSlot = null,
  bottomContent = null,
  disableFilters = false,
  onReset = null,
  isForceFilterActive = false,
  isForcedChecked = false,
  onUpdateFilters = null,
}: CheckboxHeaderItemProps) => {
  const { updateFilterOptions, updateSorting, sorting } = useMyPortfolio();
  const filterRef = useRef();
  const [showFilter, toggleShowFilter] = useToggle(false);
  const filterKey = column.column.name;
  const activeFilters = filterOption.value.filter((f) => f.isActive);
  const activeFiltersChangesCount = activeFilters.length;
  const activeFiltersWithChanges =
    (activeFiltersChangesCount < filterOption.value.length && activeFiltersChangesCount > 0) ||
    isForceFilterActive;

  const isColumnSorted = sorting.column === column.column.name;
  const sortIndicator = getSortIndicator({
    column,
    isColumnSorted,
    isDesc: sorting.order === SortOrder.DESC,
  });
  const handleUpdateFilters = (value: CheckboxFilterOption['value']) => {
    if (onUpdateFilters) {
      onUpdateFilters(value);
    } else {
      updateFilterOptions({
        columnKey: column.column.name,
        filterOption: {
          ...filterOption,
          isActive: value.filter((f) => f.isActive).length !== value.length,
          value,
        },
      });
    }
  };

  const handleUpdateSorting = (order: SortOrder) => {
    updateSorting({
      column: column.column.name,
      order,
    });
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      updateFilterOptions({
        columnKey: column.column.name,
        filterOption: {
          ...filterOption,
          value: filterOption.value.map((fo) => ({ ...fo, isActive: true })),
        },
      });
    }
  };

  return (
    <React.Fragment>
      <a
        onClick={toggleShowFilter}
        className={clsx(
          'filterTrigger',
          column.sorting && 'hasSorting',
          (activeFiltersWithChanges || showFilter) && 'active'
        )}
        ref={filterRef}
      >
        {sortIndicator}
        <span>{column.title}</span>
        <div
          className={clsx(`filterIcon`, (filterCountSlot || activeFiltersWithChanges) && 'active')}
        >
          <i className="material-icons">filter_list</i>
          {filterCountSlot}
          {!filterCountSlot && activeFiltersWithChanges && (
            <FilterCount onReset={handleReset} count={activeFiltersChangesCount} />
          )}
        </div>
      </a>

      <Popper
        id={filterKey}
        open={showFilter}
        anchorEl={filterRef.current}
        placement="bottom-start"
        style={{ zIndex: 100 }}
      >
        <ClickAwayListener onClickAway={() => toggleShowFilter(false)}>
          <CheckboxFilterBox
            classes={{ options: '' }}
            column={column.column.name}
            sortingType={column.sorting}
            sortingSelected={sorting}
            filterOptions={filterOption.value}
            filterSelected={filterOption.value}
            onUpdateFilters={handleUpdateFilters}
            onUpdateSorting={handleUpdateSorting}
            bottomContent={bottomContent}
            disableFilters={disableFilters}
            isForcedChecked={isForcedChecked}
            isAsc={isColumnSorted && sorting?.order === SortOrder.ASC}
            isDesc={isColumnSorted && sorting?.order === SortOrder.DESC}
          />
        </ClickAwayListener>
      </Popper>
    </React.Fragment>
  );
};

export default CheckboxHeaderItem;
